import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useFeedbackSubmit } from "@/hooks/useFeedbackSubmit";

export function FeedbackWidget() {
  const { user, featureFlags, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [category, setCategory] = useState("feature");
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState("");
  const [extraFile, setExtraFile] = useState<File | null>(null);
  const [extraFileUrl, setExtraFileUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mutation = useFeedbackSubmit();

  const isContentChange = category === "content_change";

  // Must be logged in to submit feedback
  if (!user) return null;

  // super_admins have the newer Requests capture tool (RequestButton), which does
  // this job better. Two bottom-right FABs is redundant clutter for the one role
  // that has both — see issue #544.
  // Safe as a bare early return: every hook above runs unconditionally and there
  // are no hooks below, unlike the RequestButton case that needed a wrapper split.
  if (isSuperAdmin) return null;

  // Determine if we're on an admin route
  const isAdmin = location.pathname.startsWith("/admin");

  // Check feature flags — default to true if flag doesn't exist yet
  const adminEnabled = featureFlags["feedback_widget_admin"] ?? true;
  const publicEnabled = featureFlags["feedback_widget_public"] ?? true;

  if (isAdmin && !adminEnabled) return null;
  if (!isAdmin && !publicEnabled) return null;

  const captureAndOpen = async () => {
    setCapturing(true);
    try {
      const canvas = await html2canvas(document.body, {
        ignoreElements: (el) => el.hasAttribute("data-feedback-widget"),
      });
      canvas.toBlob((blob) => {
        if (blob) {
          setScreenshotBlob(blob);
          setScreenshotUrl(URL.createObjectURL(blob));
        }
        setOpen(true);
        setCapturing(false);
      }, "image/png");
    } catch {
      setOpen(true);
      setCapturing(false);
    }
  };

  const resetForm = () => {
    setCategory("feature");
    setPriority("medium");
    setDescription("");
    setTarget("");
    setExtraFile(null);
    if (extraFileUrl) URL.revokeObjectURL(extraFileUrl);
    setExtraFileUrl(null);
    setFileError(null);
    setScreenshotBlob(null);
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    setScreenshotUrl(null);
  };

  // Client-side validation before we accept an attached image. A bad file
  // shouldn't reach the submit; oversized uploads are the common failure.
  const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // 8 MB
  const onExtraFileChange = (file: File | null) => {
    if (extraFileUrl) URL.revokeObjectURL(extraFileUrl);
    if (!file) {
      setExtraFile(null);
      setExtraFileUrl(null);
      setFileError(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setExtraFile(null);
      setExtraFileUrl(null);
      setFileError("Please choose an image file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setExtraFile(null);
      setExtraFileUrl(null);
      setFileError("Image is too large (max 8 MB).");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFileError(null);
    setExtraFile(file);
    setExtraFileUrl(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    mutation.mutate(
      { category, description, priority, screenshotBlob, extraFile, target: target.trim() || null },
      { onSuccess: () => { resetForm(); setOpen(false); } }
    );
  };

  // A photo/content-change request is meaningful with just an image or a
  // target pointer, so don't force a description for it. Everything else still
  // needs a written description.
  const hasContent = isContentChange
    ? Boolean(description.trim() || extraFile || target.trim())
    : Boolean(description.trim());
  const disabledReason = !hasContent
    ? (isContentChange ? "Add a description, target, or image to submit" : "Add a description to submit")
    : undefined;

  return (
    <>
      {/*
        Portal the trigger straight to <body> and keep it clickable over any
        open modal. When a Radix Dialog is open it sets `body { pointer-events:
        none }` and paints a z-50 overlay, which is what made the button dead
        while a modal (e.g. the quote Line Items editor) was up. `pointer-events-auto`
        re-enables clicks and `z-[60]` lifts it above the overlay (toasts stay at
        z-[100]). `onPointerDown` stopPropagation uses Radix's outside-interaction
        interception so opening feedback doesn't dismiss the modal underneath —
        the user's in-progress work (and the screenshot) survive.
        Known limitation: while a modal is open its focus trap + aria-hidden make
        the button mouse-only (unreachable by keyboard/screen-reader) — inherent
        to modal semantics; fixing it would require touching every modal.
      */}
      {!open && createPortal(
        <button
          data-feedback-widget
          onClick={captureAndOpen}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={capturing}
          className="pointer-events-auto fixed bottom-6 right-6 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          aria-label="Send feedback"
        >
          {capturing ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageSquarePlus className="h-5 w-5" />}
        </button>,
        document.body,
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); setOpen(v); }}>
        <DialogContent data-feedback-widget className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Feedback or Request a Change</DialogTitle>
            <DialogDescription>Report a bug, request a feature, or ask for a photo/content change.</DialogDescription>
          </DialogHeader>

          {screenshotUrl && (
            <div className="rounded-md border overflow-hidden">
              <img src={screenshotUrl} alt="Screenshot" className="w-full max-h-48 object-contain" />
            </div>
          )}

          <div className="grid gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                    <SelectItem value="content_change">Photo / content change request</SelectItem>
                    <SelectItem value="ui_ux">UI / UX</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isContentChange && (
              <div className="space-y-1.5">
                <Label htmlFor="feedback-target">What / where should change? (optional)</Label>
                <Input
                  id="feedback-target"
                  placeholder="e.g. the two duplicate photos on the drop-off page"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="feedback-description">
                {isContentChange ? "What change would you like?" : "Description"}
              </Label>
              <Textarea
                id="feedback-description"
                placeholder={isContentChange
                  ? "Describe the change — e.g. replace this photo with the one attached below."
                  : "What happened? What did you expect?"}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="feedback-file">
                {isContentChange ? "Attach the image you want us to use" : "Additional screenshot (optional)"}
              </Label>
              <Input
                id="feedback-file"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => onExtraFileChange(e.target.files?.[0] || null)}
              />
              {fileError && <p role="alert" className="text-xs text-destructive">{fileError}</p>}
              {extraFileUrl && (
                <div className="rounded-md border overflow-hidden">
                  <img src={extraFileUrl} alt="Attachment preview" className="w-full max-h-48 object-contain" />
                  {extraFile && (
                    <p className="px-2 py-1 text-xs text-muted-foreground truncate">{extraFile.name}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setOpen(false); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!hasContent || !!fileError || mutation.isPending}
              title={disabledReason}
            >
              {mutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
